import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Pagination,
  Tab,
  Tabs,
  Typography,
  styled,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFacilityLayoutData } from "@context";
import {
  InAppNotification,
  listNotifications,
  markNotificationRead,
  markNotificationsRead,
  notificationNavigateTarget,
  notificationVariantLabel,
  stashNetworkFocusFacilityId,
  stashNetworkRequestFocus,
} from "@services/notifications";
import {
  getFacilityHierarchyId,
  notifySelectedFacilitySessionUpdated,
  SELECTED_FACILITY_STORAGE_KEY,
  type FacilityHierarchyInput,
} from "@utils/facilityUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";

function findFacilityRowById(
  facilities: MyFacilityListRow[],
  facilityId: number,
): MyFacilityListRow | undefined {
  for (const row of facilities) {
    if (getFacilityHierarchyId(row as FacilityHierarchyInput) === facilityId) {
      return row;
    }
    for (const child of row.child_facilities || []) {
      if (getFacilityHierarchyId(child as FacilityHierarchyInput) === facilityId) {
        return child;
      }
    }
  }
  return undefined;
}

const PageContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 20,
  padding: "28px 32px",
  minHeight: "100%",
  background:
    "linear-gradient(180deg, #eef9fb 0%, #f5fbfc 120px, #f8fafc 100%)",
});

const HeaderRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
});

const HeaderTitle = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: 26,
  fontWeight: 700,
  color: "#033843",
  letterSpacing: "-0.02em",
});

const HeaderSub = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: 13,
  color: "#667085",
  marginTop: 4,
});

const StatPill = styled(Box)<{ $tone?: "unread" | "muted" }>(({ $tone }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 999,
  padding: "6px 12px",
  backgroundColor: $tone === "unread" ? "#fee4e2" : "#e6fafc",
  color: $tone === "unread" ? "#b42318" : "#065b70",
}));

const ListShell = styled(Box)({
  display: "flex",
  flexDirection: "column",
  borderRadius: 16,
  border: "1px solid #dce9ed",
  backgroundColor: "#fff",
  overflow: "hidden",
  boxShadow: "0 1px 2px rgba(3, 56, 67, 0.04)",
});

const NotifRow = styled(Box)<{ $unread?: boolean }>(({ $unread }) => ({
  display: "flex",
  gap: 14,
  padding: "16px 20px",
  borderBottom: "1px solid #eef2f6",
  cursor: "pointer",
  backgroundColor: $unread ? "#f0fbfd" : "#fff",
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: $unread ? "#e6fafc" : "#f8fafc",
  },
  "&:last-child": {
    borderBottom: "none",
  },
}));

const UnreadDot = styled(Box)({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: "#0a829d",
  marginTop: 7,
  flexShrink: 0,
});

const VariantChip = styled(Box)<{ $variant?: string }>(({ $variant }) => {
  const declined =
    $variant === "link_request_declined" || $variant === "unlink_request_rejected";
  const accepted = $variant === "link_request_accepted";
  return {
    fontFamily: '"Rethink Sans", sans-serif',
    fontSize: 11,
    fontWeight: 600,
    color: declined ? "#b42318" : accepted ? "#027a48" : "#065b70",
    backgroundColor: declined ? "#fee4e2" : accepted ? "#dcfae6" : "#e6fafc",
    borderRadius: 999,
    padding: "2px 10px",
    display: "inline-block",
  };
});

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

type FilterTab = "all" | "unread";

const PAGE_SIZE = 20;

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { myFacilities } = useFacilityLayoutData();
  const [items, setItems] = useState<InAppNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<FilterTab>("all");
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (pageNum: number, tab: FilterTab) => {
    setLoading(true);
    setError(null);
    try {
      const result = await listNotifications({
        limit: PAGE_SIZE,
        offset: (pageNum - 1) * PAGE_SIZE,
        unread_only: tab === "unread",
      });
      setItems(result.notifications);
      setTotal(result.total);
      setUnreadCount(result.unread_count);
    } catch (err) {
      setItems([]);
      setTotal(0);
      setError(err instanceof Error ? err.message : "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(page, filter);
  }, [load, page, filter]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleFilterChange = (_: React.SyntheticEvent, value: FilterTab) => {
    setFilter(value);
    setPage(1);
  };

  const handleOpenNotification = (n: InAppNotification) => {
    stashNetworkRequestFocus(n);
    const target = notificationNavigateTarget(n);
    const facilityId =
      n.facility_id != null && Number.isFinite(Number(n.facility_id))
        ? Number(n.facility_id)
        : null;

    if (facilityId != null) {
      stashNetworkFocusFacilityId(facilityId);
      const match = findFacilityRowById(myFacilities, facilityId);
      if (match) {
        try {
          sessionStorage.setItem(
            SELECTED_FACILITY_STORAGE_KEY,
            JSON.stringify({ ...match, id: match.facility_id ?? match.id }),
          );
          notifySelectedFacilitySessionUpdated();
        } catch {
          // ignore
        }
      }
      if (target.state) {
        target.state = {
          ...target.state,
          selectedFacilityId: facilityId,
          selectedFacility: match
            ? { ...match, id: match.facility_id ?? match.id, facility_id: match.facility_id }
            : { facility_id: facilityId, id: facilityId },
        };
      }
    }

    // Navigate first; mark-read in background so facility switch is not delayed.
    navigate(
      { pathname: target.path, search: target.search ?? "" },
      target.state ? { state: target.state } : undefined,
    );

    if (!n.is_read) {
      setItems((prev) =>
        prev.map((row) =>
          row.id === n.id
            ? { ...row, is_read: true, read_at: new Date().toISOString() }
            : row
        )
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      void markNotificationRead(n.id)
        .then((res) => setUnreadCount(res.unread_count))
        .catch(() => undefined);
    }
  };

  const handleMarkAll = async () => {
    if (unreadCount <= 0 || marking) return;
    setMarking(true);
    try {
      const res = await markNotificationsRead({ mark_all: true });
      setUnreadCount(res.unread_count);
      setItems((prev) => prev.map((row) => ({ ...row, is_read: true })));
      if (filter === "unread") {
        setPage(1);
        void load(1, "unread");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to mark all read");
    } finally {
      setMarking(false);
    }
  };

  return (
    <PageContainer>
      <HeaderRow>
        <Box>
          <HeaderTitle>Notifications</HeaderTitle>
          <HeaderSub>Facility network alerts and link request updates</HeaderSub>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <StatPill $tone="unread">{unreadCount} unread</StatPill>
          <StatPill $tone="muted">{total} {filter === "unread" ? "shown" : "total"}</StatPill>
          <Button
            size="small"
            disabled={unreadCount <= 0 || marking}
            onClick={() => void handleMarkAll()}
            sx={{
              textTransform: "none",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              color: "#065b70",
              border: "1px solid #b8dce5",
              borderRadius: 2,
              px: 1.5,
              "&:hover": { backgroundColor: "#e6fafc", borderColor: "#0a829d" },
            }}
          >
            {marking ? "Marking…" : "Mark all read"}
          </Button>
        </Box>
      </HeaderRow>

      <Tabs
        value={filter}
        onChange={handleFilterChange}
        sx={{
          minHeight: 36,
          "& .MuiTab-root": {
            fontFamily: '"Rethink Sans", sans-serif',
            textTransform: "none",
            fontWeight: 600,
            minHeight: 36,
            color: "#667085",
          },
          "& .Mui-selected": { color: "#065b70 !important" },
          "& .MuiTabs-indicator": { backgroundColor: "#0a829d" },
        }}
      >
        <Tab value="all" label="All" />
        <Tab value="unread" label={`Unread${unreadCount ? ` (${unreadCount})` : ""}`} />
      </Tabs>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} sx={{ color: "#065b70" }} />
        </Box>
      ) : items.length === 0 ? (
        <ListShell>
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: 15,
                fontWeight: 600,
                color: "#033843",
                mb: 0.5,
              }}
            >
              {filter === "unread" ? "All caught up" : "No notifications yet"}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: 13,
                color: "#667085",
              }}
            >
              {filter === "unread"
                ? "You have no unread notifications."
                : "Facility network actions will show up here."}
            </Typography>
          </Box>
        </ListShell>
      ) : (
        <ListShell>
          {items.map((n) => (
            <NotifRow
              key={n.id}
              $unread={!n.is_read}
              onClick={() => void handleOpenNotification(n)}
              role="button"
              aria-label={`Open ${notificationVariantLabel(n.variant)}`}
            >
              {n.is_read ? <Box sx={{ width: 8, flexShrink: 0 }} /> : <UnreadDot />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    mb: 0.75,
                  }}
                >
                  <VariantChip $variant={n.variant}>
                    {notificationVariantLabel(n.variant)}
                  </VariantChip>
                  {!n.is_read ? (
                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans", sans-serif',
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#0a829d",
                        letterSpacing: "0.04em",
                      }}
                    >
                      NEW
                    </Typography>
                  ) : null}
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: 12,
                      color: "#98a2b3",
                      ml: "auto",
                    }}
                  >
                    {formatWhen(n.created_at)}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: 15,
                    fontWeight: n.is_read ? 500 : 700,
                    color: "#033843",
                    mb: 0.5,
                  }}
                >
                  {n.title || "Notification"}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: 13,
                    color: "#475467",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.45,
                  }}
                >
                  {n.body || "—"}
                </Typography>
                {(n.facility_name || n.action_taken) && (
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: 12,
                      color: "#667085",
                      mt: 1,
                    }}
                  >
                    {[n.facility_name, n.action_taken].filter(Boolean).join(" · ")}
                  </Typography>
                )}
              </Box>
            </NotifRow>
          ))}
        </ListShell>
      )}

      {!loading && total > PAGE_SIZE ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 0.5 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                fontFamily: '"Rethink Sans", sans-serif',
              },
            }}
          />
        </Box>
      ) : null}
    </PageContainer>
  );
};

export default NotificationsPage;
